import { useEffect, useMemo, useState } from 'react'
import { RowSelectionState } from '@tanstack/react-table'

import { useTable } from '@/ui/Table'
import { useCombinedLogsColumns } from './useCombinedLogsColumns'
import { useWorkstationStore } from '@/modules/Workstation/store'
import { useManageCombinedLogsColumns } from './useManageCombinedLogsColumns'
import { useAuthStore } from '@/store/auth-store'
import { Role } from '@/types/auth'

import { useGetSharedLogs } from '../api/useGetControllerLogs'
import { useGetCodes } from '../api/useGetCodes'

import { generateCodeTypeMap } from '../helpers/generateCodeTypeMap'
import { reorderLinkedLogs } from '../helpers/reorderLinkedLogs'
import { groupReassignedLogs } from '../helpers/groupReassignedLogs'
import { filterLogsByType } from '../helpers/filterLogsByType'
import { sortLogsByTime } from '../helpers/sortLogsByTime'

import { useCombinedLogsTableFormStateStore, useCombinedLogsTableStateStore } from '../store'

export const useSharedLogsTable = () => {
    const { filter: globalFilter, setFilterCodesMap } = useCombinedLogsTableStateStore()
    const { reassignedRoutes } = useWorkstationStore()
    const { selectedRowId, setSelectedRowId } = useCombinedLogsTableFormStateStore()
    const { data, isLoading: isDataLoading } = useGetSharedLogs()
    const { data: codes = [], isLoading: isCodesLoading } = useGetCodes()
    const { role } = useAuthStore()
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

    const isManualSelectionEnabled = role === Role.Reader

    const logs = useMemo(() => {
        if (!data) {
            return []
        }

        const sortedLogs = [...data].sort(sortLogsByTime)
        return reorderLinkedLogs(sortedLogs)
    }, [data])

    const reassignedLogs = useMemo(() => {
        if (!logs.length || role !== Role.Controller) {
            return []
        }

        return groupReassignedLogs(logs, reassignedRoutes)
    }, [logs, reassignedRoutes, role])

    const columns = useCombinedLogsColumns(isDataLoading, codes)
    const columnVisibility = useManageCombinedLogsColumns()

    const table = useTable({
        data: logs,
        columns,
        enableGlobalFilter: true,
        globalFilterFn: filterLogsByType,
        onRowSelectionChange: setRowSelection,
        getRowId: (row) => row.id,
        enableMultiRowSelection: isManualSelectionEnabled,
        state: {
            rowSelection,
            globalFilter,
            columnVisibility,
        },
    })

    const reassignedTable = useTable({
        data: reassignedLogs,
        columns,
        onRowSelectionChange: setRowSelection,
        getRowId: (row) => row.id,
        enableMultiRowSelection: isManualSelectionEnabled,
        state: {
            rowSelection,
            columnVisibility,
        },
    })

    useEffect(() => {
        if (Object.keys(rowSelection).length !== 0) {
            setSelectedRowId(Object.keys(rowSelection)[0])
        }
    }, [rowSelection])

    useEffect(() => {
        if (!codes.length) {
            return
        }

        const codesMap = generateCodeTypeMap(codes)
        setFilterCodesMap(codesMap)
    }, [codes])

    useEffect(() => {
        if (selectedRowId && !rowSelection[selectedRowId]) {
            table.setRowSelection({ [selectedRowId]: true })
            setSelectedRowId(null)
        }
    }, [selectedRowId])

    return {
        table,
        reassignedTable,
        columns,
        isLoading: isDataLoading || isCodesLoading,
        isManualSelectionEnabled,
        rowSelection,
        setRowSelection,
        hasReassignedLogs: !!reassignedLogs.length,
    }
}
